import axios from 'axios';
import { requestQueueManager } from './requestQueueManager';
import { sessionManager } from './sessionManager';
import { getStoredAccessToken } from './accessTokenStore';
import { getLastLoginSuccessAt } from './authEpoch';

// Get API base URL from environment variable
const baseURL =
  (import.meta as unknown as { env?: { VITE_API_URL?: string } }).env
    ?.VITE_API_URL || '';

/**
 * Axios instance for API requests
 * - baseURL from env
 * - withCredentials for cookie-based auth
 * - basic interceptors for logging
 */
export const api = axios.create({
  baseURL: `${baseURL}/api`,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
});

// Request interceptor (for logging, future token logic, deduplication)
api.interceptors.request.use(
  config => {
    // Mark request start time to detect stale 401s (e.g. from requests that started before login)
    (config as { __startedAt?: number }).__startedAt = Date.now();
    return config;
  },
  error => Promise.reject(error)
);

// Response interceptor (for logging, error handling)
api.interceptors.response.use(
  response => {
    return response;
  },
  error => {
    if (error.response?.status === 401) {
      const isLoginRequest =
        error.config?.url?.includes('/auth/login') ||
        error.config?.url?.includes('auth/login');
      const isRefreshRequest =
        error.config?.url?.includes('/auth/refresh_token') ||
        error.config?.url?.includes('auth/refresh_token');
      const isOnLoginPage =
        typeof window !== 'undefined' &&
        (window.location.pathname === '/login' ||
          window.location.pathname.startsWith('/login'));
      const isRetry = (error.config as { _retry?: boolean } | undefined)?._retry;
      const isProfileRequest =
        error.config?.url?.includes('/v1/users/profile') ||
        error.config?.url?.includes('v1/users/profile');

      if (isProfileRequest) {
        // Suppress reauth for stale 401s: requests that started before the last successful login.
        // This avoids a race where an old profile request (e.g. from useCurrentUser boot) returns
        // 401 after the user has just logged in, incorrectly triggering the session-expired modal.
        const lastLoginSuccessAt = getLastLoginSuccessAt();
        const startedAt =
          (error.config as { __startedAt?: number } | undefined)?.__startedAt ?? 0;
        const isStaleRequest =
          lastLoginSuccessAt != null && startedAt > 0 && startedAt < lastLoginSuccessAt;

        if (isStaleRequest) {
          return Promise.reject(error);
        }

        // Profile 401 is expected during unauthenticated boot (fresh tab / first load).
        // Only trigger the "session expired" UX if we know the user has authenticated before
        // and we're not currently on the login page.
        const hasAuthenticatedBefore =
          typeof window !== 'undefined' &&
          window.localStorage.getItem('hasAuthenticated') === 'true';

        // If we have a stored fallback access token, the app can often recover by doing a
        // silent refresh and retrying the profile request. Avoid forcing reauth in that case.
        const hasFallbackAccessToken = !!getStoredAccessToken();

        if (!isOnLoginPage && hasAuthenticatedBefore && !hasFallbackAccessToken) {
          console.warn('[HTTP] 401 on profile request, triggering reauth');
          sessionManager.triggerReauth('Profile request unauthorized');
        }
        return Promise.reject(error);
      }

      console.warn('[HTTP] 401 response', {
        url: error.config?.url,
        isLoginRequest,
        isRefreshRequest,
        isRetry,
        isOnLoginPage,
      });

      if (isRefreshRequest) {
        console.warn('[HTTP] 401 on refresh token, triggering reauth');
        sessionManager.triggerReauth('Refresh token unauthorized');
        return Promise.reject(error);
      }

      if (!isRetry && !isLoginRequest && !isRefreshRequest && !isOnLoginPage && !isProfileRequest) {
        return requestQueueManager.handleUnauthorized(error);
      }
    }

    return Promise.reject(error);
  }
);
